import { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw } from "lucide-react";
import { getAiSummary } from "../lib/api";
import { toast } from "sonner";

/**
 * Trace Insight ✨ — primary intelligence panel.
 * Auto-fetches; cached server-side per session.
 */
export default function TraceAiInsight({ projectId, sessionId, eligible, onLoaded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId || !sessionId || !eligible) return;
    let active = true;
    setLoading(true);
    setError(null);
    getAiSummary(projectId, sessionId)
      .then((d) => {
        if (!active) return;
        setData(d);
        if (d?.ai_summary) onLoaded?.(d.ai_summary);
      })
      .catch((e) => {
        if (active) setError(e?.response?.data?.detail || "Insight unavailable");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [projectId, sessionId, eligible, onLoaded]);

  const onRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const d = await getAiSummary(projectId, sessionId, true);
      setData(d);
      if (d?.ai_summary) onLoaded?.(d.ai_summary);
      toast.success("Insight refreshed");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Insight failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  if (!eligible) return null;

  const ai = data?.ai_summary;

  return (
    <div
      data-testid="trace-ai"
      className="mt-6 rounded-2xl p-5 sm:p-6 relative overflow-hidden border border-indigo-100"
      style={{
        background:
          "linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(59,130,246,0.05) 100%)",
      }}
    >
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-30 bg-gradient-to-br from-blue-400 to-indigo-500 blur-3xl pointer-events-none" />

      <div className="relative flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-indigo-500 shadow-sm">
            <Sparkles size={12} className="text-white" strokeWidth={2.4} />
          </span>
          <span className="text-[12px] uppercase tracking-[0.18em] text-indigo-700 font-bold">
            Trace Insight
          </span>
        </div>
        <button
          data-testid="trace-ai-refresh"
          onClick={onRefresh}
          disabled={refreshing || loading}
          className="text-[#4b5563] hover:text-[#111827] transition disabled:opacity-50"
          title="Regenerate insight"
        >
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} strokeWidth={2.2} />}
        </button>
      </div>

      <div className="relative">
        {loading && !ai ? (
          <div className="space-y-2">
            <div className="h-4 rounded bg-indigo-100/60 w-3/4 animate-pulse" />
            <div className="h-4 rounded bg-indigo-100/60 w-2/3 animate-pulse" />
            <div className="h-4 rounded bg-indigo-100/60 w-1/2 animate-pulse" />
          </div>
        ) : error ? (
          <p className="text-[13px] text-rose-600">{error}</p>
        ) : data?.skipped ? (
          <p className="text-[13px] text-[#6b7280] italic">{data.reason}</p>
        ) : ai ? (
          <div className="space-y-3 text-[14px] leading-relaxed text-[#0f0a3d]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-indigo-700 font-bold mb-0.5">
                You were building
              </div>
              <p className="font-medium">{ai.building}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-indigo-700 font-bold mb-0.5">
                What changed
              </div>
              <p className="font-medium">{ai.changed}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-indigo-700 font-bold mb-0.5">
                Next step
              </div>
              <p className="font-medium">{ai.next_step}</p>
            </div>
          </div>
        ) : null}
      </div>

      {ai && (
        <div className="relative mt-4 pt-3 border-t border-indigo-100 flex items-center justify-between gap-2 text-[11px] text-indigo-600 font-medium">
          <span className="flex items-center gap-1.5">
            <Sparkles size={10} strokeWidth={2.4} /> Based on your recent commits
          </span>
          {ai.category && (
            <span className="px-2 py-0.5 rounded-full bg-white border border-indigo-200 text-indigo-700 font-semibold">
              {ai.category}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
